import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { EngineTheme, useMergeClasses, useTheme } from '../../../../theme';
import { SeatMapPermissions, SeatMapRestrictions } from '@websky/graphql';
import { useRestrictionsConditionForKV } from '../hooks';
import { useCallback } from 'react';
import { SafeHtml } from '@shared/ui';

interface Props {
	className?: string;
	classes?: EngineTheme['SeatMap']['SeatsInfoStyles'];
	restrictions?: SeatMapRestrictions[];
	permissions?: SeatMapPermissions[];
	additional?: string[];
}

const SeatAdditionalInfo: React.FC<Props> = ({ className, classes, restrictions, permissions, additional }) => {
	const { SeatsInfoStyles: defaultClasses } = useTheme('SeatMap');
	const theme = useMergeClasses(defaultClasses, classes);
	const { t } = useTranslation('SeatMap');

	const filterRestrictions = React.useMemo(
		() => restrictions?.filter(restriction => !!t(`Restriction_${restriction}`)),
		[restrictions]
	);

	const renderList = useCallback(({ title, list }: { title: string; list?: string[] }) => {
		if (!list?.length) {
			return null;
		}

		return (
			<>
				<div className={theme.additionalInfo__title}>{title}:</div>
				<ul>
					{list.map(item => (
						<SafeHtml key={item} tag="li" html={item} />
					))}
				</ul>
			</>
		);
	}, []);

	if (!filterRestrictions?.length && !permissions?.length && !additional?.length) {
		return null;
	}

	return (
		<div className={cn(theme.additionalInfo, className)}>
			{renderList({ title: t('Information for the place'), list: additional })}

			{useRestrictionsConditionForKV() &&
				renderList({
					title: t('This seat is forbidden for'),
					list: filterRestrictions.map(restriction => t(`Restriction_${restriction}`))
				})}

			{renderList({
				title: t('This seat is allowed for'),
				list: permissions.map(permission => t(`Restriction_${permission}`))
			})}
		</div>
	);
};

export default SeatAdditionalInfo;
